<template>
  <div class="editor-wrap" v-if="activeComponent && isSupportedEditor">
    <component
      :is="activeComponent"
      :node="selectedNode"
      :scene-id="sceneId"
      @preview="$emit('preview', $event)"
      @show-error-popup="$emit('showErrorPopup', $event)"
      :key="selectedNode.placeholder?.id || selectedNode.id"
    />

    <div class="tags-wrap">
      <span>Tags (Separate by ;) </span>
      <IdmInput
        :key="tagsKey"
        :placeholder="'Intro;Outro'"
        :tags="true"
        :tagsList="selectedNode.tags"
        @inputTags="inputTags"
        data-test="editors-tags-input"
      />
    </div>

    <div class="description-wrap">
      <span>Description</span>
      <IdmTextarea
        :text="selectedNode.description"
        :placeholder="'Add description ...'"
        @input="inputDescription"
        data-test="placeholder-description-textarea"
      />
    </div>
  </div>
</template>

<script>
import textEditor from '@/components/editors/text/index.vue';
import mediaEditor from '@/components/editors/media/index.vue';
import audioEditor from '@/components/editors/audio/index.vue';
import { useBuilderStore } from "@/stores/builderStore";
import sceneCacheStorage from '@/helper/cache';
import _ from 'lodash';
export default {
  name: 'editors',

  props: ['selectedNode', 'sceneId'],

  components: {
    textEditor,
    mediaEditor,
    audioEditor,
  },

  data() {
    return {
      supportedTypes: ['textEditor', 'mediaEditor', 'audioEditor'],
    };
  },

  computed: {
    builderStore() {
      return useBuilderStore();
    },
    scene() {
      return this.builderStore.scene;
    },
    // IdmInput builds its tag chips imperatively and only reads tagsList in created(),
    // so it can't reflect an external change to the tag list on its own. Keying it on the
    // node plus the store's reset signal forces a fresh re-mount (which re-reads tagsList)
    // whenever the selected node changes or the tags are reset via "Clear changes"/undo,
    // while leaving the key stable during the user's own typing so input focus is kept.
    // A prior fix tried to drive this re-mount from a `selectedNode.tags` watcher, but
    // path-watching the reassigned prop's nested array did not fire reliably on a second
    // clear, leaving a stale chip on screen (VP-12732).
    tagsKey() {
      return `${this.selectedNode?.key}-${this.builderStore.tagsResetSignal}`;
    },
    activeComponent() {
      const media =
        this.selectedNode.media_type === 'video' ||
        this.selectedNode.media_type === 'color' ||
        this.selectedNode.media_type === 'image';
      return this.selectedNode
        ? media
          ? 'mediaEditor'
          : this.selectedNode.media_type + 'Editor'
        : null;
    },
    isSupportedEditor() {
      return this.supportedTypes.indexOf(this.activeComponent) >= 0;
    },
  },

  methods: {
    inputTags(tagsData) {
      const tags = _.reject(tagsData, (tag) => {
        return tag === '';
      });
      this.builderStore.updateNodeTags({
        node: this.selectedNode,
        tags: tags,
      });
      sceneCacheStorage.updateSceneCache(this.scene);
    },
    inputDescription(e) {
      this.builderStore.updateNodeDescription({
        node: this.selectedNode,
        description: e.target.value.trim(),
      });
      sceneCacheStorage.updateSceneCache(this.scene);
    },
  },
};
</script>

<style lang="scss" scoped>
.tags-wrap,
.description-wrap {
  color: var(--dark-500);
  margin-top: 24px;
  padding-top: 21px;
  border-top: 1px solid #dbdbdb;

  .idm-input-container,
  .idm-textarea {
    margin-top: 11px;
  }
}
</style>
